import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { milestones, testimonials } from '@/mocks/comics';

export default function AboutPage() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [location.pathname]);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于yy漫画 - 正版授权漫画平台',
    description: 'yy漫画是专注正版授权的在线漫画阅读平台，汇聚热门连载、完结漫画海量资源。了解yy漫画的品牌故事、发展历程和联系方式。',
    url: 'https://yytoon.github.io/about',
    isPartOf: {
      '@type': 'WebSite',
      name: 'yy漫画',
      url: 'https://yytoon.github.io',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'yy漫画',
    url: 'https://yytoon.github.io',
    description: '正版授权在线漫画阅读平台，为全球漫迷提供优质漫画阅读体验。',
    foundingDate: '2018',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-6666',
      contactType: 'customer service',
      email: 'contact@yymanhua.com',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '漫画创意产业园A座12层',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      postalCode: '100000',
      addressCountry: 'CN',
    },
    sameAs: [
      'https://weibo.com/yymanhua',
      'https://space.bilibili.com/yymanhua',
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>

      <Navbar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={breadcrumbItems} />

          <div className="px-4 md:px-6">
            {/* 品牌介绍 */}
            <section className="py-10 md:py-14">
              <div className="text-center max-w-3xl mx-auto">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
                  关于 yy漫画
                </h1>
                <p className="mt-2 text-sm text-foreground-500">
                  正版授权 · 品质阅读 · 漫迷首选
                </p>
                <div className="mt-8 p-8 md:p-10 rounded-2xl bg-background-100 border border-background-200/70 text-left">
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    yy漫画成立于2018年，是一家专注正版授权的在线漫画阅读平台。我们致力于为中国漫迷提供最优质的漫画阅读体验，汇聚了来自国内外超过100家出版社的海量正版漫画资源。
                  </p>
                  <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                    平台涵盖<strong>恋爱、热血、悬疑、奇幻、搞笑、科幻、都市、古风</strong>等多种类型，无论你喜欢什么样的故事，都能在yy漫画找到心仪的作品。我们始终坚持&ldquo;正版授权、品质至上&rdquo;的理念，用心服务每一位漫迷。
                  </p>
                  <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                    截至2025年底，yy漫画已拥有超过1000万注册用户，漫画资源超过20000部，日活跃用户超过300万。我们不仅提供优质的阅读内容，更不断优化阅读体验，推出多端同步、AI智能推荐等创新功能，让阅读更加便捷愉悦。
                  </p>
                </div>
              </div>
            </section>

            {/* 核心数据 */}
            <section className="py-10 md:py-14 border-t border-background-200/70">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {[
                  { value: '1000万+', label: '注册用户' },
                  { value: '20000+', label: '漫画资源' },
                  { value: '100+', label: '合作出版社' },
                  { value: '99.7%', label: '用户好评率' },
                ].map((stat, i) => (
                  <div key={i} className="text-center p-6 rounded-xl bg-background-100 border border-background-200/70">
                    <p className="font-heading text-3xl md:text-4xl font-bold text-primary-600">{stat.value}</p>
                    <p className="mt-2 text-sm text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* 发展历程 */}
            <section className="py-10 md:py-14 border-t border-background-200/70">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
                发展历程
              </h2>
              <div className="relative max-w-3xl mx-auto">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
                <div className="space-y-8">
                  {milestones.map((m, i) => (
                    <div key={i} className={`relative flex items-start gap-6 md:gap-10 ${
                      i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}>
                      <div className={`hidden md:block flex-1 ${i % 2 === 0 ? 'text-right' : 'text-left'}`}>
                        <span className="font-heading text-2xl font-bold text-primary-600">{m.year}</span>
                      </div>
                      <div className="relative z-10 w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 shrink-0 md:absolute md:left-1/2 md:-translate-x-1/2">
                        <span className="w-2.5 h-2.5 rounded-full bg-background-50"></span>
                      </div>
                      <div className="flex-1 bg-background-100 border border-background-200/70 rounded-xl p-4 md:p-5">
                        <span className="md:hidden font-heading text-lg font-bold text-primary-600">{m.year}</span>
                        <p className="text-sm text-foreground-700 mt-1 md:mt-0">{m.event}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* 用户口碑 */}
            <section className="py-10 md:py-14 border-t border-background-200/70">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                {testimonials.map((t) => (
                  <div key={t.id} className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                        <div className="flex items-center gap-0.5 mt-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <i
                              key={i}
                              className={`text-xs ${i < t.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-foreground-300'}`}
                            ></i>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* 联系方式 */}
            <section className="py-10 md:py-14 border-t border-background-200/70">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 max-w-4xl mx-auto">
                <div className="flex flex-col items-center text-center p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-mail-line text-2xl text-primary-600"></i>
                  </div>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-900">商务合作</h3>
                  <p className="mt-2 text-sm text-foreground-500">business@yymanhua.com</p>
                </div>
                <div className="flex flex-col items-center text-center p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100">
                    <i className="ri-phone-line text-2xl text-accent-600"></i>
                  </div>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-900">客服热线</h3>
                  <p className="mt-2 text-sm text-foreground-500">400-888-6666</p>
                  <p className="text-xs text-foreground-400 mt-1">工作日 9:00-18:00</p>
                </div>
                <div className="flex flex-col items-center text-center p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-secondary-100">
                    <i className="ri-map-pin-line text-2xl text-secondary-600"></i>
                  </div>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-900">公司地址</h3>
                  <p className="mt-2 text-sm text-foreground-500">北京市朝阳区</p>
                  <p className="text-sm text-foreground-500">漫画创意产业园A座12层</p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}